import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { scopeKindOf } from "../settings/scope";
import { CATALOG } from "../settings/catalog";
import { useSettings } from "../settings/context";
import { useEvent } from "../context/EventProvider";
import { emailsFamily } from "../settings/emailsFamily";
import {
  indexCatalog,
  searchCatalog,
  type SearchHit,
} from "../settings/search";
import { ORDER_KEY, orderedEntries, writeOrder } from "../funnel/order";
import { EVERY_SCREEN, FUNNEL, withContext } from "../routes/registry";
import { Group, rememberedOpen, rememberOpen } from "./Group";
import { SaveMode } from "./SaveMode";
import { SearchField, SearchResults } from "./PanelSearch";
import { useInPlaceKeys } from "./InPlace";
import { SITUATIONS, useSituationControl } from "./situation";
import "./panel.css";

/** La famille du parcours, dans « Le site » : voir `FUNNEL_GROUP` dans le catalogue. */
const FUNNEL_FAMILY = "Étapes du parcours";

/**
 * La famille « Emails » s'ajoute à l'onglet « Le site » à l'affichage, et pas dans le
 * catalogue : voir `settings/emailsFamily.ts`.
 */
const SITE_FAMILIES = [...(CATALOG.site ?? []), emailsFamily()];

/**
 * THE ORGANISER PANEL.
 *
 * Two families, exactly the agreed framing: "Le site" (what applies everywhere)
 * and "Cette page" (what belongs to the screen on display). The tab is STICKY: it
 * does not jump when the organiser moves between screens.
 *
 * Contents are GENERATED from the catalog. Adding a setting never requires
 * touching this file.
 *
 * Au-dessus des deux onglets, une RECHERCHE qui parcourt tout le catalogue, par libellé
 * ou par le texte affiché sur le site, et mène à la ligne trouvée (`PanelSearch.tsx`).
 *
 * It holds no READING control: which screen, as which typology, on which device
 * all live in the organiser bar; the language lives in the site itself. What
 * remains here is what you CHANGE — plus the statement of where the change
 * lands, which four competing sources make indispensable.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function Panel({
  page,
  instance,
}: {
  page: string;
  /** L'instance d'étape répétable regardée — voir `funnel/order.ts`. */
  instance?: string;
}) {
  /* La famille dépliée, une seule — retenue d'une visite à l'autre. */
  const [open, setOpen] = useState<string | null>(rememberedOpen);
  const [tab, setTab] = useState<"site" | "page">("site");
  /** La recherche en cours ; vide, le panneau montre ses familles. */
  const [query, setQuery] = useState("");
  /** La ligne que la recherche vient de désigner, surlignée jusqu'au geste suivant. */
  const [aimed, setAimed] = useState<string | null>(null);
  const { scope, languages, typos, forms, value, refused } = useSettings();
  const event = useEvent();
  const navigate = useNavigate();
  const { context } = useParams();
  const { situation, setSituation } = useSituationControl();
  const inPage = useInPlaceKeys();

  const declared = tab === "site" ? SITE_FAMILIES : (CATALOG[page] ?? null);
  const isStep = FUNNEL.some((s) => s.key === page);

  const openFamily = (title: string | null) => {
    setOpen(title);
    rememberOpen(title);
  };

  /**
   * L'INDEX DE LA RECHERCHE, avec le même contexte que les familles (`Group.tsx`) : les
   * typologies, les formulaires et le parcours rangé. Refait seulement quand ils
   * changent ; les VALEURS, elles, sont lues au moment de chercher.
   */
  const range = orderedEntries(value(ORDER_KEY));
  const entries = useMemo(
    () =>
      indexCatalog({
        typos: typos.flatMap((t) =>
          t.id === null ? [] : [{ id: t.id, name: t.name }],
        ),
        forms,
        steps: range,
      }),
    // `range` se refait à chaque rendu : c'est sa VALEUR qui compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typos, forms, writeOrder(range)],
  );
  const hits = query.trim()
    ? searchCatalog(entries, query, value, { eventName: event?.name })
    : [];

  /** Montrer le parcours, depuis une étape du tunnel ou un résultat de recherche. */
  const showFunnel = () => {
    setQuery("");
    setTab("site");
    openFamily(FUNNEL_FAMILY);
    setAimed(ORDER_KEY);
  };

  /**
   * ALLER AU RÉGLAGE TROUVÉ : le bon onglet, le bon écran, la bonne famille ouverte, et
   * la ligne surlignée. Un réglage masqué mène à ce qui le masque : l'interrupteur ou
   * le sélecteur de sa famille, ou le parcours pour une étape retirée.
   */
  const pick = (hit: SearchHit) => {
    if (hit.hidden?.by === "step") {
      showFunnel();
      return;
    }
    setQuery("");
    /* Un réglage d'une autre version de la confirmation (le titre du refus, vu depuis
       « Sur place ») : on passe à la situation où il se voit, sinon la ligne cherchée
       n'est pas dans le panneau. */
    const situations = hit.setting.situations;
    if (situations && situation && !situations.includes(situation))
      setSituation(situations[0]!);
    if (hit.scope === "site") setTab("site");
    else {
      setTab("page");
      if (hit.scope !== page) {
        const path = EVERY_SCREEN.find((s) => s.key === hit.scope)?.path;
        if (path) navigate(withContext(path, context));
      }
    }
    openFamily(hit.group);
    setAimed(
      hit.hidden?.by === "switch" || hit.hidden?.by === "mode"
        ? hit.hidden.key
        : hit.aimKey,
    );
  };

  /**
   * A FAMILY WHOSE SWITCH IS OFF GOVERNS NOTHING, so it goes away — the same rule
   * that hides a single dormant setting, applied one level up. It is needed there
   * because several settings of a step already carry a `needs` of their own, and a
   * setting can only have one: a step taken out of the funnel puts its title, its
   * answers and its automatisms to sleep all at once.
   *
   * ⚠️ AND IT GOES WITHOUT A NOTICE. Each sleeping family used to name itself and its
   * cause, on the reasoning that a family vanishing in silence reads as a bug. In
   * practice a step puts THREE families to sleep at once, so that became three lines
   * repeating one sentence — and the cause is already on screen, in the "Étapes du
   * parcours" family right above, where the switch was just turned off. Removed at
   * the organiser's request; the reasoning holds only where the cause is out of
   * sight, which here it is not.
   */
  const groups = declared?.filter((g) => {
    const needs = typeof g.needs === "function" ? g.needs(instance) : g.needs;
    return !needs || value(needs) !== false;
  });
  const languageName =
    languages.find((l) => l.id === scope.languageId)?.name ?? "";
  const typoName = typos.find((t) => t.id === scope.typoId)?.name ?? "";
  /* Où atterrit la prochaine écriture : voir `settings/scope.ts`. Partagée avec le
     bandeau du châssis, qui doit dire exactement la même chose. */
  const scopeKind = scopeKindOf(scope);

  return (
    <aside className="panel" aria-label="Réglages">
      <SearchField
        query={query}
        onQuery={(q) => {
          setQuery(q);
          setAimed(null);
        }}
        onSubmit={() => hits[0] && pick(hits[0])}
      />

      {query.trim() ? (
        <SearchResults query={query} hits={hits} onPick={pick} />
      ) : (
        <>
          <div
            className="panel__tabs"
            role="group"
            aria-label="Portée des réglages"
          >
            <button
              type="button"
              aria-pressed={tab === "site"}
              onClick={() => {
                setTab("site");
                setAimed(null);
              }}
            >
              Le site
            </button>
            <button
              type="button"
              aria-pressed={tab === "page"}
              onClick={() => {
                setTab("page");
                setAimed(null);
              }}
            >
              Cette page
            </button>
          </div>

          <div className="panel__b">
            {/* ⚠️ LE SERVEUR REFUSE D'ÉCRIRE — dit UNE FOIS, ici, et pas par bouton.
                L'enregistrement et la traduction exigent tous deux un rôle sur le
                domaine : sans lui, le panneau se règle à l'écran et rien ne part. Sans
                ce constat, l'organisateur voyait un badge « Échec » qui promettait une
                nouvelle tentative, et un bouton « Traduire » qui ne disait rien du
                tout — mesuré, écran muet sur un 401. Un panneau qui a l'air de
                fonctionner et n'enregistre pas est le pire des deux mondes. */}
            {refused && (
              <div className="panel__no" role="alert">
                <b>Vos modifications ne partent pas</b>
                <span>
                  Le serveur les refuse : ce compte n'a pas les droits d'écriture sur
                  cet événement. Tout ce que vous réglez reste visible à l'écran, et
                  sera perdu au rechargement.
                </span>
              </div>
            )}

            {/**
             * ═══ LA PORTÉE D'ÉCRITURE, DITE FRANCHEMENT ═══
             *
             * Sans elle, l'organisateur ne peut pas savoir si sa prochaine modification
             * vaut pour tout le monde ou pour une seule typologie.
             *
             * ⚠️ TROIS ÉTATS, PAS DEUX, et c'est le deuxième qui manquait. Seule la
             * TYPOLOGIE se voyait ; écrire dans une AUTRE LANGUE que la référence est
             * pourtant une surcharge exactement au même titre, et le bandeau était alors
             * gris, de la même couleur que la référence. On règle vingt lignes en anglais
             * en croyant régler le site. Demandé par l'user : « si on est dans un mode de
             * surcharge, il faudrait le mettre plus en avant. »
             *
             * ⚠️ ET LE MOT EST ÉCRIT, pas seulement la couleur. Une teinte se remarque
             * quand on la compare ; l'étiquette « Surcharge » se lit sans point de
             * comparaison, et c'est le cas de quelqu'un qui vient d'ouvrir le panneau.
             */}
            <div className="ctx" data-scope={scopeKind}>
              <b>
                {scopeKind !== "reference" && (
                  <span className="ctx__tag">
                    {/* ⚠️ DES COUCHES, ET C'EST LE DESSIN JUSTE : une surcharge est un
                        calque POSÉ SUR la référence, pas un avertissement. Le triangle
                        d'alerte aurait dit « attention, danger » là où il n'y en a aucun —
                        écrire pour une seule langue est un geste normal, il faut seulement
                        savoir qu'on le fait. */}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 3 3 7.5l9 4.5 9-4.5z" />
                      <path d="M3 14.5 12 19l9-4.5" />
                    </svg>
                    Surcharge
                  </span>
                )}
                {scope.typoId
                  ? `Vous modifiez ${typoName} · ${languageName}`
                  : `Vous modifiez : ${languageName}`}
              </b>
              <span>
                {scope.typoId
                  ? "Ce que vous changez ici ne vaut que pour cette typologie, dans cette langue. Le reste du site n'en saura rien."
                  : scopeKind === "reference"
                    ? "C'est la version de référence : elle sert de repli à toutes les autres."
                    : "Ce que vous changez ici ne vaut que pour cette langue. Les autres gardent la version de référence."}
              </span>
            </div>

            <SaveMode />

            {/* ═══ LA VERSION DE LA CONFIRMATION QU'ON REGARDE ═══
            Cinq écrans en un, selon la réponse du participant : le choix montre la
            version dans l'aperçu et ne garde ici que ses réglages. Dans le panneau et
            pas dans la barre : la barre est pleine (elle débordait de 66 à 152 px entre
            1 200 et 1 600 px de large, mesuré), et c'est ici qu'il filtre. Voir
            `panel/situation.tsx`. */}
        {tab === "page" && page === "confirmation" && situation && (
          <label className="psituation">
            <span>Situation affichée</span>
            <select
              value={situation}
              onChange={(e) =>
                setSituation(e.target.value as typeof situation)
              }
            >
              {SITUATIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Les titres et messages de l'écran se modifient dans l'aperçu : on le dit
            une fois, ici, plutôt que de laisser découvrir le geste par hasard. */}
        {tab === "page" && inPage.size > 0 && (
          <div className="pnote">
            <span>
              Les titres et messages de cet écran se modifient{" "}
              <b>en cliquant dessus dans l'aperçu</b>.
            </span>
          </div>
        )}

        {/* ⚠️ LE PARCOURS NE SE RÉPÈTE PLUS EN TÊTE DE CHAQUE ÉTAPE : il se règle une
                fois, dans « Le site ». Une ligne le rappelle ici, sur l'écran même où l'on
                se demande comment retirer ou renommer cette étape. */}
            {tab === "page" && isStep && (
              <div className="pnote">
                <span>
                  Les étapes, leur ordre et leurs noms se règlent dans{" "}
                  <b>Le site › {FUNNEL_FAMILY}</b>.
                </span>
                <button type="button" onClick={showFunnel}>
                  Ouvrir le parcours
                </button>
              </div>
            )}

            {groups && groups.length > 0 ? (
              groups.map((group) => (
                <Group
                  key={group.title}
                  group={group}
                  {...(instance ? { instance } : {})}
                  aimed={aimed}
                  open={open === group.title}
                  /* Ouvrir une famille referme celle d'avant : le panneau vise deux cent
                     cinquante réglages, et plusieurs familles dépliées le rendent aussi
                     long que s'il n'y avait pas de familles du tout. Recliquer sur la
                     famille ouverte la referme — sinon il devient impossible de revenir à
                     la table des matières. */
                  onToggle={() => {
                    openFamily(open === group.title ? null : group.title);
                    setAimed(null);
                  }}
                />
              ))
            ) : groups && isStep ? (
              /* Toutes les familles d'une étape dorment quand l'étape n'est pas servie.
                 Avant, le parcours restait affiché au-dessus et disait pourquoi ; il vit
                 maintenant dans « Le site », la raison doit donc être écrite ici. */
              <p className="norule">
                <b>Cette étape n'est pas servie.</b>
                <br />
                Elle n'apparaît pas dans le parcours des participants. Ses réglages
                reviennent dès qu'elle y est remise.
              </p>
            ) : (
              /* The panel SAYS SO instead of standing empty: a screen with no settings
                 of its own is not a broken screen. */
              <p className="norule">
                <b>Cet écran n’a pas de réglage propre.</b>
                <br />
                Il s’affiche tel quel. Ce qui le concerne — la marque, le pied de
                page — se règle dans <b>Le site</b>.
              </p>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
